// Full-moon instants from Meeus, Astronomical Algorithms ch. 49 ("Phases of
// the Moon"), truncated to the 15 largest periodic terms — error stays under
// an hour, so mapping an instant to a calendar day is reliable. A plain
// synodic modulo drifts up to ±0.7 day and would misplace the highlighted day.

export const SYNODIC_MONTH = 29.530588861

const UNIX_EPOCH_JD = 2440587.5
const MS_PER_DAY = 86400000

const rad = d => (d * Math.PI) / 180

function phaseArguments(k) {
  const T = k / 1236.85
  const E = 1 - 0.002516 * T - 0.0000074 * T ** 2

  return {
    T,
    E,
    M: rad(2.5534 + 29.1053567 * k - 0.0000014 * T ** 2 - 0.00000011 * T ** 3),
    Mp: rad(201.5643 + 385.81693528 * k + 0.0107582 * T ** 2 + 0.00001238 * T ** 3 - 0.000000058 * T ** 4),
    F: rad(160.7108 + 390.67050284 * k - 0.0016118 * T ** 2 - 0.00000227 * T ** 3 + 0.000000011 * T ** 4),
    O: rad(124.7746 - 1.56375588 * k + 0.0020672 * T ** 2 + 0.00000215 * T ** 3),
  }
}

function meanPhaseJD(k, T) {
  return 2451550.09766 + SYNODIC_MONTH * k + 0.00015437 * T ** 2 - 0.00000015 * T ** 3 + 0.00000000073 * T ** 4
}

export function jdFromDate(date) {
  return date.getTime() / MS_PER_DAY + UNIX_EPOCH_JD
}

export function dateFromJD(jd) {
  return new Date((jd - UNIX_EPOCH_JD) * MS_PER_DAY)
}

// JDE of the full moon of lunation k (k = 0 ≈ first new moon of 2000).
export function fullMoonJD(k) {
  const kf = k + 0.5
  const { T, E, M, Mp, F, O } = phaseArguments(kf)

  let jde = meanPhaseJD(kf, T)

  jde +=
    -0.40614 * Math.sin(Mp) +
    0.17302 * E * Math.sin(M) +
    0.01614 * Math.sin(2 * Mp) +
    0.01043 * Math.sin(2 * F) +
    0.00734 * E * Math.sin(Mp - M) -
    0.00515 * E * Math.sin(Mp + M) +
    0.00209 * E * E * Math.sin(2 * M) -
    0.00111 * Math.sin(Mp - 2 * F) -
    0.00057 * Math.sin(Mp + 2 * F) +
    0.00056 * E * Math.sin(2 * Mp + M) -
    0.00042 * Math.sin(3 * Mp) +
    0.00042 * E * Math.sin(M + 2 * F) +
    0.00038 * E * Math.sin(M - 2 * F) -
    0.00024 * E * Math.sin(2 * Mp - M) -
    0.00017 * Math.sin(O)

  return jde // TT ≈ UTC (ΔT ~70 s, negligible at day granularity)
}

// JDE of the new moon of lunation k. New and full moons use different
// periodic corrections; subtracting half a mean lunation is not accurate
// enough to assign the event to a civil calendar day reliably.
export function newMoonJD(k) {
  const { T, E, M, Mp, F, O } = phaseArguments(k)
  let jde = meanPhaseJD(k, T)

  jde +=
    -0.4072 * Math.sin(Mp) +
    0.17241 * E * Math.sin(M) +
    0.01608 * Math.sin(2 * Mp) +
    0.01039 * Math.sin(2 * F) +
    0.00739 * E * Math.sin(Mp - M) -
    0.00514 * E * Math.sin(Mp + M) +
    0.00208 * E * E * Math.sin(2 * M) -
    0.00111 * Math.sin(Mp - 2 * F) -
    0.00057 * Math.sin(Mp + 2 * F) +
    0.00056 * E * Math.sin(2 * Mp + M) -
    0.00042 * Math.sin(3 * Mp) +
    0.00042 * E * Math.sin(M + 2 * F) +
    0.00038 * E * Math.sin(M - 2 * F) -
    0.00024 * E * Math.sin(2 * Mp - M) -
    0.00017 * Math.sin(O) -
    0.00007 * Math.sin(Mp + 2 * M) +
    0.00004 * Math.sin(2 * Mp - 2 * F) +
    0.00004 * Math.sin(3 * M) +
    0.00003 * Math.sin(Mp + M - 2 * F) +
    0.00003 * Math.sin(2 * Mp + 2 * F) -
    0.00003 * Math.sin(Mp + M + 2 * F) +
    0.00003 * Math.sin(Mp - M + 2 * F) -
    0.00002 * Math.sin(Mp - M - 2 * F) -
    0.00002 * Math.sin(3 * Mp + M) +
    0.00002 * Math.sin(4 * Mp)

  return jde
}

function lastPhaseK(date, phaseJD) {
  const jd = jdFromDate(date)
  let k = Math.floor((jd - 2451550.09766) / SYNODIC_MONTH)
  while (phaseJD(k + 1) <= jd) k++
  while (phaseJD(k) > jd) k--
  return k
}

function lastFullMoonK(date) {
  return lastPhaseK(date, fullMoonJD)
}

function lastNewMoonK(date) {
  return lastPhaseK(date, newMoonJD)
}

// UTC instant of the most recent full moon at or before `date`.
export function lastFullMoonBefore(date) {
  return dateFromJD(fullMoonJD(lastFullMoonK(date)))
}

// UTC instant of the first full moon strictly after `date`.
export function nextFullMoonAfter(date) {
  return dateFromJD(fullMoonJD(lastFullMoonK(date) + 1))
}

// UTC instant of the most recent new moon at or before `date`.
export function lastNewMoonBefore(date) {
  return dateFromJD(newMoonJD(lastNewMoonK(date)))
}

// UTC instant of the first new moon strictly after `date`.
export function nextNewMoonAfter(date) {
  return dateFromJD(newMoonJD(lastNewMoonK(date) + 1))
}

// Phase anchored to a known full-moon instant so glyphs stay self-consistent
// with the highlighted day. `age` is days since new moon, in [0, SYNODIC_MONTH).
export function moonPhaseFromAnchor(date, fullMoonInstant) {
  const half = SYNODIC_MONTH / 2
  let age = ((jdFromDate(date) - jdFromDate(fullMoonInstant) + half) % SYNODIC_MONTH + SYNODIC_MONTH) % SYNODIC_MONTH
  const illumination = (1 - Math.cos((2 * Math.PI * age) / SYNODIC_MONTH)) / 2
  return { age, illumination, waxing: age < half }
}

export function moonPhase(date) {
  return moonPhaseFromAnchor(date, lastFullMoonBefore(date))
}
